import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search,
  CreditCard,
  Bug,
  HelpCircle,
  MessageCircle,
  Mail,
  ChevronRight,
  ChevronDown,
  BookOpen,
  Mic
} from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const SUPPORT_EMAIL = 'support@tenses.ai';

/**
 * Help Centre.
 *
 * Articles are real question/answer pairs that expand in place - previously
 * they were plain strings rendered as rows with a chevron, so nothing happened
 * when you clicked them.
 *
 * The answers describe how the platform actually behaves: the 80% accuracy
 * gate, the reset-on-failure rule, Razorpay checkout, GST handling, and the
 * mic permissions the speaking modules need.
 */
const HELP_CATEGORIES = [
  {
    id: 'getting-started',
    icon: HelpCircle,
    title: 'Getting Started',
    description: 'New to Tenses? Start here.',
    articles: [
      {
        q: 'How do I create an account?',
        a: 'Go to app.tenses.ai and register with your email address. You will receive a one-time code to verify the address, after which you can set your password and sign in. If your college or employer has enrolled you, your account is created for you and you will receive login instructions by email instead.'
      },
      {
        q: 'How do I choose a plan?',
        a: 'Open the Pricing page and pick the duration that suits you - 3, 6 or 12 months. Every plan unlocks the same thing: all 11 learning modules, the AI interview simulator and TPI certification. Longer plans simply cost less per month. Colleges and teams should use the Enterprise option and contact us for volume licensing.'
      },
      {
        q: 'How does practice work?',
        a: 'Pick any module and practise as many questions as you like. Every attempt is scored by AI straight away, and each score feeds a running "accuracy" figure for that module. Practice is unlimited and never penalised - it exists purely to get you ready for the assessment.'
      },
      {
        q: 'How do I unlock a module assessment?',
        a: 'An assessment unlocks once your accuracy score for that module reaches 80% and you have completed the required number of practice questions. This gate is deliberate: it is what makes a completed module mean something.'
      },
      {
        q: 'What happens if I fail an assessment?',
        a: 'You need 80% to pass. If you fall short, your accuracy score for that module resets to zero and you go back to practice before you can retake it. It is strict on purpose - it prevents guessing your way to a certificate.'
      },
      {
        q: 'How do I earn the Tenses certificate?',
        a: 'Clear all 11 module assessments, then sit the TPI Grand Assessment. Passing produces your Tenses Proficiency Index score (0–1200 across eight dimensions) and an official certificate with a shareable verification link.'
      }
    ]
  },
  {
    id: 'payment',
    icon: CreditCard,
    title: 'Payments & Billing',
    description: 'Plans, invoices and GST.',
    articles: [
      {
        q: 'Which payment methods are accepted?',
        a: 'Payments are processed securely through Razorpay, so you can pay by credit card, debit card, UPI or net banking. We never see or store your card details.'
      },
      {
        q: 'When does my access start?',
        a: 'Immediately. As soon as Razorpay confirms the payment your plan is activated and you can start practising in the same session. There is no waiting period and no free trial.'
      },
      {
        q: 'How is GST applied?',
        a: 'All listed prices are exclusive of tax. GST at 18% is added at checkout - for example, the 3-month plan is ₹1,500 + ₹270 GST = ₹1,770 payable.'
      },
      {
        q: 'Can I extend my plan?',
        a: 'Yes. You can buy more time at any point, and any access you have left is added on top of the new duration, so nothing you have already paid for is lost.'
      },
      {
        q: 'How do I get an invoice?',
        a: `Razorpay emails a payment receipt as soon as the transaction completes. If you need a GST invoice for reimbursement, write to ${SUPPORT_EMAIL} with your registered email address and we will send one.`
      },
      {
        q: 'What is the refund policy?',
        a: `All sales are final - we do not offer refunds. Your plan unlocks the entire platform the moment payment is confirmed, so purchases are non-refundable whether or not you use them, and we cannot offer partial refunds or credit for unused time. The only exceptions are a duplicate charge or a sustained technical failure on our side; in either case email ${SUPPORT_EMAIL} and we will investigate.`
      }
    ]
  },
  {
    id: 'modules',
    icon: BookOpen,
    title: 'Modules & Scoring',
    description: 'How each module is marked.',
    articles: [
      {
        q: 'Which modules are included?',
        a: 'All 11: Sentence Reading, Sentence Repeating, Just a Minute, Story Retelling, Jumbled Sentences, Grammar, Error Identification, Passage Comprehension, Short Stories, Essay Writing and Email Writing. They cover speaking, listening, reading, grammar and writing.'
      },
      {
        q: 'How are speaking modules scored?',
        a: 'You record your answer and the audio is analysed for pronunciation, fluency, accuracy and completeness. Reading and repeating tasks are compared against the target sentence; Just a Minute is judged on how well you sustain fluent, relevant speech.'
      },
      {
        q: 'How are writing modules scored?',
        a: 'Essays and emails are read by AI against a fixed rubric covering subject line, greeting, body, sign-off, tone and grammar, and returned as a single overall percentage. Email Writing expects between 150 and 1500 characters.'
      },
      {
        q: 'Why can I not see feedback during an assessment?',
        a: 'In practice you get instant feedback after every question, including the correct answer. In an assessment answers are recorded silently and the result appears only at the end - otherwise the assessment would simply be another practice round.'
      },
      {
        q: 'What is the TPI score?',
        a: 'The Tenses Proficiency Index scores you from 0 to 1200 across eight dimensions: Fluency, Pronunciation, Thoroughness, Vocabulary, Grammar, Writing Skills, Comprehension and Attention to Detail. Each dimension is worth up to 150 points.'
      }
    ]
  },
  {
    id: 'interview',
    icon: Mic,
    title: 'AI Interview',
    description: 'Practising interviews.',
    articles: [
      {
        q: 'How does the AI interview work?',
        a: 'Choose a role, and the system generates seven questions for it - one introduction, four technical and two behavioural. You answer each one out loud, exactly as you would in a real interview.'
      },
      {
        q: 'How is my interview scored?',
        a: 'Your spoken answers are transcribed and assessed on relevance, structure, clarity and depth. You receive an overall score plus written feedback for each individual question, so you can see precisely which answers let you down.'
      },
      {
        q: 'Can I retake an interview?',
        a: 'Yes, as many times as you like while your plan is active. Questions are generated fresh for each attempt, so you cannot simply memorise answers.'
      },
      {
        q: 'Do I need a microphone?',
        a: 'Yes. The interview is spoken, so you will be asked for microphone permission the first time. On mobile, if you decline by mistake you can grant it again from the prompt in the app or from your device settings.'
      }
    ]
  },
  {
    id: 'bug-report',
    icon: Bug,
    title: 'Troubleshooting',
    description: 'Something not working?',
    articles: [
      {
        q: 'My microphone is not being picked up',
        a: 'Check that the browser or app has microphone permission and that no other application is holding the mic. On mobile, grant permission when prompted, or enable it in your device settings and reopen the app. Wired headsets are usually more reliable than Bluetooth for recording.'
      },
      {
        q: 'My score did not save',
        a: 'Scores are submitted as soon as an attempt finishes, so a dropped connection is the usual cause. Check your connection and try again - if a submission fails the app tells you rather than pretending it worked, so you can retry without losing the attempt.'
      },
      {
        q: 'A question failed to load',
        a: 'This is nearly always a temporary network issue. Use the "Try again" button on the error screen to reload the question. If it keeps happening on the same module, let us know which one.'
      },
      {
        q: 'My progress is not updating',
        a: 'Accuracy and solved counts refresh when you return to the dashboard. Pull down to refresh the screen; if the figures still look wrong after that, send us your registered email and the module name.'
      },
      {
        q: 'How do I report a bug?',
        a: `Email ${SUPPORT_EMAIL} with the module you were using, what you expected to happen, what actually happened, and a screenshot if you have one. Your device and browser or app version help us reproduce it quickly.`
      }
    ]
  },
  {
    id: 'account',
    icon: MessageCircle,
    title: 'Account & Settings',
    description: 'Managing your account.',
    articles: [
      {
        q: 'How do I reset my password?',
        a: 'Choose "Forgot password" on the sign-in screen and enter your registered email. We send a reset link that stays valid for 15 minutes. If it expires, simply request another.'
      },
      {
        q: 'Can I change my email address?',
        a: `Write to ${SUPPORT_EMAIL} from your current registered address and tell us the new one. We update it manually so that your scores, certificates and remaining access all move across correctly.`
      },
      {
        q: 'Can I use Tenses on my phone and laptop?',
        a: 'Yes. The same account works on the web app and the mobile app, and your progress, accuracy scores and certificates are shared between them.'
      },
      {
        q: 'My institution enrolled me - how do I sign in?',
        a: 'Use the email address your college or employer registered. You will have received login instructions by email; if you never set a password, use "Forgot password" on that address to create one.'
      },
      {
        q: 'How do I delete my account?',
        a: `Email ${SUPPORT_EMAIL} from your registered address and ask us to delete it. We remove your personal data; note that this also permanently removes your scores and any certificate you have earned.`
      }
    ]
  }
];

/** A single expandable Q&A row. */
const ArticleAccordion = ({ article, isOpen, onToggle, id }) => (
  <div
    className="overflow-hidden rounded-xl bg-white transition-all"
    style={{
      border: `1px solid ${isOpen ? 'rgba(52,89,164,0.28)' : 'var(--border-light)'}`,
      boxShadow: isOpen ? 'var(--shadow-md)' : 'var(--shadow-xs)'
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`${id}-panel`}
      id={`${id}-button`}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
    >
      <span
        className="text-[0.96rem] font-medium"
        style={{ color: isOpen ? 'var(--tenses-blue)' : 'var(--text-primary)' }}
      >
        {article.q}
      </span>
      <ChevronDown
        className="h-5 w-5 shrink-0 transition-transform duration-300"
        style={{
          color: isOpen ? 'var(--tenses-blue)' : 'var(--text-muted)',
          transform: isOpen ? 'rotate(180deg)' : 'none'
        }}
        aria-hidden="true"
      />
    </button>

    {/* Grid-rows trick animates height without needing a fixed pixel value. */}
    <div
      id={`${id}-panel`}
      role="region"
      aria-labelledby={`${id}-button`}
      className="grid transition-all duration-300 ease-out"
      style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
    >
      <div className="overflow-hidden">
        <p
          className="px-5 pb-5 text-[0.9rem] leading-relaxed"
          style={{ color: 'var(--text-secondary)' }}
        >
          {article.a}
        </p>
      </div>
    </div>
  </div>
);

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openArticle, setOpenArticle] = useState(null);
  const location = useLocation();

  const activeCategory = location.hash ? location.hash.replace('#', '') : null;
  const selectedCategory = HELP_CATEGORIES.find((cat) => cat.id === activeCategory);

  // Search runs across every category so people can find an answer without
  // knowing which section it lives in.
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const hits = [];
    HELP_CATEGORIES.forEach((cat) => {
      cat.articles.forEach((article, i) => {
        if (
          article.q.toLowerCase().includes(q) ||
          article.a.toLowerCase().includes(q)
        ) {
          hits.push({ ...article, category: cat.title, key: `${cat.id}-${i}` });
        }
      });
    });
    return hits;
  }, [searchQuery]);

  const toggle = (key) => setOpenArticle((cur) => (cur === key ? null : key));

  return (
    <div className="min-h-screen">
      <Header />

      <main>
        <section className="section" style={{ paddingTop: 'clamp(7rem, 12vw, 9rem)' }}>
          <header className="section-header" style={{ marginBottom: '2.5rem' }}>
            <p className="eyebrow">Help centre</p>
            <h1 className="heading-2 mt-3">How can we help?</h1>
            <p className="body-large">
              Search the answers below, or email us and a person will reply.
            </p>
          </header>

          {/* Search */}
          <div className="mx-auto mb-12 max-w-[560px]">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setOpenArticle(null);
                }}
                placeholder="Search for an answer…"
                className="w-full rounded-full py-3.5 pl-12 pr-4 outline-none transition-shadow focus:ring-2"
                style={{ border: '1px solid var(--border-medium)', background: '#fff' }}
              />
            </div>
          </div>

          <div className="mx-auto max-w-[820px]">
            {/* ---------------------------- Search ---------------------------- */}
            {searchResults ? (
              <div>
                <p className="mb-5 text-[0.9rem]" style={{ color: 'var(--text-secondary)' }}>
                  {searchResults.length
                    ? `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for “${searchQuery}”`
                    : `No results for “${searchQuery}”.`}
                </p>

                {searchResults.length ? (
                  <div className="space-y-3">
                    {searchResults.map((article) => (
                      <div key={article.key}>
                        <p
                          className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em]"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          {article.category}
                        </p>
                        <ArticleAccordion
                          id={article.key}
                          article={article}
                          isOpen={openArticle === article.key}
                          onToggle={() => toggle(article.key)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="rounded-2xl p-8 text-center"
                    style={{ background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}
                  >
                    <p className="body-medium">
                      Try a different word, or{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--tenses-blue)', fontWeight: 600 }}>
                        email us
                      </a>{' '}
                      and we will answer directly.
                    </p>
                  </div>
                )}
              </div>
            ) : selectedCategory ? (
              /* -------------------------- One category ------------------------- */
              <div>
                <Link
                  to="/help"
                  className="mb-6 inline-block text-sm font-medium"
                  style={{ color: 'var(--tenses-blue)' }}
                >
                  ← Back to Help Centre
                </Link>

                <div className="mb-8 flex items-center gap-4">
                  <div
                    className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl"
                    style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
                  >
                    <selectedCategory.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="heading-3">{selectedCategory.title}</h2>
                    <p className="text-[0.92rem]" style={{ color: 'var(--text-secondary)' }}>
                      {selectedCategory.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedCategory.articles.map((article, i) => {
                    const key = `${selectedCategory.id}-${i}`;
                    return (
                      <ArticleAccordion
                        key={key}
                        id={key}
                        article={article}
                        isOpen={openArticle === key}
                        onToggle={() => toggle(key)}
                      />
                    );
                  })}
                </div>

                <ContactCallout />
              </div>
            ) : (
              /* --------------------------- All categories ---------------------- */
              <div>
                <h2 className="heading-3 mb-5">Browse by category</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {HELP_CATEGORIES.map((category) => (
                    <Link
                      key={category.id}
                      to={`/help#${category.id}`}
                      className="card group flex flex-col"
                    >
                      <span
                        className="mb-4 grid h-11 w-11 place-items-center rounded-xl"
                        style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
                      >
                        <category.icon className="h-5 w-5" />
                      </span>
                      <h3 className="text-[1rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {category.title}
                      </h3>
                      <p className="mt-1.5 flex-1 text-[0.88rem]" style={{ color: 'var(--text-secondary)' }}>
                        {category.description}
                      </p>
                      <span
                        className="mt-4 inline-flex items-center gap-1 text-[0.85rem] font-semibold transition-all group-hover:gap-2"
                        style={{ color: 'var(--tenses-blue)' }}
                      >
                        {category.articles.length} articles
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </Link>
                  ))}
                </div>

                <ContactCallout />
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

/** Shared "still stuck?" panel. */
const ContactCallout = () => (
  <div
    className="mt-10 rounded-2xl p-6 sm:p-7"
    style={{ background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}
  >
    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-4">
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
          style={{ background: '#fff', color: 'var(--tenses-blue)' }}
        >
          <Mail className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-[1rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
            Still need help?
          </h3>
          <p className="mt-1 text-[0.88rem]" style={{ color: 'var(--text-secondary)' }}>
            Email us and a person will get back to you, usually within a working day.
          </p>
        </div>
      </div>
      <Link to="/contact" className="btn-primary shrink-0" style={{ minHeight: 44, padding: '0 22px', fontSize: '0.9rem' }}>
        Contact us
      </Link>
    </div>
  </div>
);

export default HelpCenterPage;
